import { ORG_ROUTES, STORE_ROUTES } from '@/config/router';
import type { QuickStartTaskId, QuickStartTaskResult } from '@/lib/api/setup.types';

export type QuickStartGoTarget = { kind: 'wizard'; href: string } | { kind: 'route'; href: string };

const wizardHref = (orgSlug: string, storeId: string | null, step: string) => {
  const base = ORG_ROUTES(orgSlug).SETUP();
  const params = new URLSearchParams({ mode: 'wizard', step });
  if (storeId) params.set('storeId', storeId);
  return `${base}?${params.toString()}`;
};

/**
 * Direct CRM routes for Quick Start tasks.
 * Keep aligned with `src/lib/quick-start/goTargetPaths.ts` (tested in Jest).
 */
const routeForTask = (taskId: QuickStartTaskId, orgSlug: string, storeId: string | null): string | null => {
  const org = ORG_ROUTES(orgSlug);
  if (!storeId) {
    if (taskId === 'store') return org.STORE_CREATE();
    return null;
  }
  const store = STORE_ROUTES(orgSlug, storeId);
  const routes: Record<QuickStartTaskId, string> = {
    store: store.STORE_EDIT(),
    catalog: store.PRODUCTS(),
    shipping: store.SHIPPINGS(),
    payment: store.PAYMENT_FLOWS(),
    checkout: store.STOREFRONT(),
    paypal: store.INTEGRATION_CONFIG('paypal'),
    apple_pay: store.INTEGRATION_CONFIG('apple_pay'),
    google_pay: store.INTEGRATION_CONFIG('google_pay'),
    taxes: store.TAXES(),
    domain: org.DOMAINS(),
    // The recovery flow is an abandoned-checkout email template — send merchants
    // straight to the template editor to create one, not the templates list.
    emails: store.EMAILS_CREATE(),
    customer_portal: store.CLUB_SETTINGS(),
    fraud_protection: store.INTEGRATION_CONFIG('chargeblast'),
    upsells: store.UPSELLS(),
    marketing_pixels: store.INTEGRATIONS(),
    webhooks: store.INTEGRATION_CONFIG('webhooks'),
  };
  return routes[taskId] ?? null;
};

export const buildQuickStartGoTarget = (
  task: QuickStartTaskResult,
  orgSlug: string,
  storeId: string | null,
): QuickStartGoTarget | null => {
  if (task.status === 'locked') return null;
  const route = routeForTask(task.id, orgSlug, storeId);
  if (route) return { kind: 'route', href: route };
  if (task.wizardStep) return { kind: 'wizard', href: wizardHref(orgSlug, storeId, task.wizardStep) };
  return null;
};

export const buildQuickStartLearnMoreUrl = (helpBase: string, path?: string) =>
  path ? `${helpBase.replace(/\/$/, '')}${path}` : helpBase;
